import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Avatar, Button, Card, Col, Divider, Empty, Input, InputNumber, List, Row,
    Select, Space, Statistic, Table, Tag, Tooltip, Typography,
} from "antd";
import {
    UserOutlined, SearchOutlined, PlusOutlined, DeleteOutlined, SaveOutlined,
    CheckCircleFilled, WarningFilled, SolutionOutlined, ArrowLeftOutlined,
    FileTextOutlined, PercentageOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import axiosInstance from "../../variables/api/axiosInstance";

const { Title, Text } = Typography;

const PRIMARY = "#242166";
const SUCCESS = "#16a34a";
const DANGER = "#dc2626";

const fcfa = (n) => `${Number(n || 0).toLocaleString("fr-FR")} FCFA`;
const dossierNum = (d) => d?.numSinistre || d?.numeroSinistreM || d?.numeroSinistreP || "—";
// N° de sinistre de la COMPAGNIE (ancien système) : affiché sous le n° PoolIns pour que les
// utilisateurs encore sur leur ancien référentiel s'y retrouvent. Masqué s'il est absent ou
// identique au n° déjà affiché.
const dossierCie = (d) => {
    const m = (d?.numeroSinistreM || "").trim();
    return m && m !== dossierNum(d) ? m : "";
};

/**
 * Règlement multiple des experts — v2. Workflow modernisé (AntD v5) :
 * 1) choisir l'expert + taux de retenue ; 2) ajouter ses dossiers ;
 * 3) saisir honoraires/frais (contrôle SAP) ; 4) enregistrer le règlement cumulé.
 * Mêmes endpoints que la v1 : /api/expertise/expert/all, /api/sinistre/expertise/{id},
 * POST /api/decaissement/all-experts.
 */
const GestionPaiementSinistreExpertsV2 = () => {
    const navigate = useNavigate();

    const [experts, setExperts] = useState([]);
    const [expert, setExpert] = useState(null);
    const [tauxRetenue, setTauxRetenue] = useState(5);

    const [sinistres, setSinistres] = useState([]);
    const [loadingDossiers, setLoadingDossiers] = useState(false);
    const [searchDossier, setSearchDossier] = useState("");

    const [selection, setSelection] = useState([]); // dossiers à régler
    const [saving, setSaving] = useState(false);

    // ── Chargement des experts ────────────────────────────────────────────────
    useEffect(() => {
        axiosInstance.get("/api/expertise/expert/all")
            .then((res) => setExperts(Array.isArray(res.data) ? res.data : (res.data?.content ?? [])))
            .catch((e) => { console.error(e); toast.error("Erreur lors du chargement des experts."); });
    }, []);

    // ── Sélection d'un expert → chargement de ses dossiers ────────────────────
    const handleSelectExpert = (codeId) => {
        const exp = experts.find((x) => x.codeId === codeId) || null;
        setExpert(exp);
        setSelection([]);
        setSearchDossier("");
        setTauxRetenue(exp?.tauxRetenue ?? 5);
        if (!exp) { setSinistres([]); return; }

        setLoadingDossiers(true);
        axiosInstance.get(`/api/sinistre/expertise/${exp.codeId}`)
            .then((res) => {
                const data = Array.isArray(res.data)
                    ? res.data
                    : res.data?.content ?? res.data?.data ?? res.data?.items ?? [];
                setSinistres(data);
            })
            .catch((e) => { console.error(e); toast.error("Erreur lors du chargement des dossiers."); })
            .finally(() => setLoadingDossiers(false));
    };

    // ── Panier de règlement ───────────────────────────────────────────────────
    const ajouter = (dossier) => {
        if (selection.find((d) => d.codeId === dossier.codeId)) return;
        setSelection((prev) => [...prev, { ...dossier, honoraires: 0, fraisAccessoires: 0 }]);
    };
    const retirer = (codeId) => setSelection((prev) => prev.filter((d) => d.codeId !== codeId));
    const setMontant = (codeId, champ, valeur) =>
        setSelection((prev) => prev.map((d) => d.codeId === codeId ? { ...d, [champ]: Number(valeur) || 0 } : d));

    const estValide = (d) => (Number(d.honoraires || 0) + Number(d.fraisAccessoires || 0)) <= Number(d.sap || 0);

    const dossiersDisponibles = useMemo(() => {
        const q = searchDossier.trim().toLowerCase();
        return sinistres.filter((d) =>
            !selection.find((s) => s.codeId === d.codeId) &&
            (!q || dossierNum(d).toLowerCase().includes(q) || dossierCie(d).toLowerCase().includes(q))
        );
    }, [sinistres, selection, searchDossier]);

    const totaux = useMemo(() => {
        const totalHonoraires = selection.reduce((a, d) => a + Number(d.honoraires || 0), 0);
        const totalFrais = selection.reduce((a, d) => a + Number(d.fraisAccessoires || 0), 0);
        const totalBrut = totalHonoraires + totalFrais;
        // La retenue ne porte QUE sur les honoraires ; les frais accessoires sont payés à valeur faciale.
        const montantRetenue = totalHonoraires * (tauxRetenue / 100);
        const totalNet = totalBrut - montantRetenue;
        return { totalHonoraires, totalFrais, totalBrut, montantRetenue, totalNet };
    }, [selection, tauxRetenue]);

    const erreurs = selection.filter((d) => !estValide(d)).length;
    const peutEnregistrer = selection.length > 0 && erreurs === 0 && totaux.totalBrut > 0;

    const enregistrer = () => {
        if (!peutEnregistrer || !expert) return;
        setSaving(true);
        const payload = selection.map((d) => ({
            codeId: d.codeId,
            honoraires: Number(d.honoraires || 0),
            fraisAccessoires: Number(d.fraisAccessoires || 0),
            retenue: tauxRetenue,
            expertCodeId: expert.codeId,
        }));
        axiosInstance.post("/api/decaissement/all-experts", JSON.stringify(payload), {
            headers: { "Content-Type": "application/json" },
        })
            .then(() => {
                toast.success("Règlement enregistré avec succès.");
                setSelection([]);
                setExpert(null);
                setSinistres([]);
            })
            .catch((e) => { console.error(e); toast.error("Erreur lors de l'enregistrement du règlement."); })
            .finally(() => setSaving(false));
    };

    const expertOptions = useMemo(() => experts.map((e) => ({
        value: e.codeId,
        label: `${e.prenom || ""} ${e.nom || ""}`.trim(),
        fonction: e.fonction,
        taux: e.tauxRetenue,
    })), [experts]);

    // ── Colonnes du tableau « Dossiers à régler » ─────────────────────────────
    const columns = [
        {
            title: "Dossier", key: "dossier", width: 190,
            render: (_, d) => (
                <div>
                    <Text strong style={{ fontFamily: "monospace" }}>{dossierNum(d)}</Text>
                    {dossierCie(d) && (
                        <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>
                            N°&nbsp;cie&nbsp;: {dossierCie(d)}
                        </div>
                    )}
                    <div style={{ fontSize: 12, color: "#9ca3af" }}>SAP&nbsp;: {fcfa(d.sap)}</div>
                </div>
            ),
        },
        {
            title: "Honoraires", key: "honoraires", width: 150,
            render: (_, d) => (
                <InputNumber
                    style={{ width: "100%" }} min={0} value={d.honoraires || 0}
                    controls={false} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                    parser={(v) => `${v}`.replace(/\s/g, "")}
                    onChange={(v) => setMontant(d.codeId, "honoraires", v)}
                />
            ),
        },
        {
            title: "Frais accessoires", key: "frais", width: 150,
            render: (_, d) => (
                <InputNumber
                    style={{ width: "100%" }} min={0} value={d.fraisAccessoires || 0}
                    controls={false} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                    parser={(v) => `${v}`.replace(/\s/g, "")}
                    onChange={(v) => setMontant(d.codeId, "fraisAccessoires", v)}
                />
            ),
        },
        {
            title: "Total", key: "total", width: 150, align: "right",
            render: (_, d) => {
                const total = Number(d.honoraires || 0) + Number(d.fraisAccessoires || 0);
                const ok = estValide(d);
                return (
                    <div>
                        <Text strong style={{ color: ok ? PRIMARY : DANGER }}>{fcfa(total)}</Text>
                        <div>
                            {ok
                                ? <Tag color="success" icon={<CheckCircleFilled />} style={{ marginInlineEnd: 0 }}>OK</Tag>
                                : <Tag color="error" icon={<WarningFilled />} style={{ marginInlineEnd: 0 }}>Dépasse le SAP</Tag>}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "", key: "action", width: 44, align: "center",
            render: (_, d) => (
                <Tooltip title="Retirer">
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => retirer(d.codeId)} />
                </Tooltip>
            ),
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"SINISTRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* ── En-tête ── */}
                            <div style={{
                                background: `linear-gradient(120deg, ${PRIMARY} 0%, #3b378f 100%)`,
                                borderRadius: 16, padding: "22px 26px", marginBottom: 18,
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                flexWrap: "wrap", gap: 12, boxShadow: "0 6px 18px rgba(36,33,102,0.22)",
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                    <div style={{
                                        width: 50, height: 50, borderRadius: 12, background: "rgba(255,255,255,0.15)",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>
                                        <SolutionOutlined style={{ fontSize: 26, color: "#fff" }} />
                                    </div>
                                    <div>
                                        <Title level={3} style={{ color: "#fff", margin: 0 }}>Règlement des experts</Title>
                                        <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                            Règlement cumulé des honoraires d'expertise
                                        </Text>
                                    </div>
                                </div>
                                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ borderRadius: 8 }}>
                                    Retour
                                </Button>
                            </div>

                            <Row gutter={[18, 18]}>
                                {/* ── Colonne gauche : sélection ── */}
                                <Col xs={24} lg={9}>
                                    <Card
                                        bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
                                        title={<Space><UserOutlined style={{ color: PRIMARY }} /><span>1. Expert &amp; retenue</span></Space>}
                                    >
                                        <Text type="secondary" style={{ fontSize: 13 }}>Expert</Text>
                                        <Select
                                            showSearch allowClear style={{ width: "100%", marginTop: 6 }}
                                            placeholder="Rechercher un expert…" optionFilterProp="label"
                                            value={expert?.codeId} onChange={(v) => handleSelectExpert(v)}
                                            options={expertOptions}
                                            optionRender={(opt) => (
                                                <div>
                                                    <div style={{ fontWeight: 600 }}>{opt.data.label}</div>
                                                    <div style={{ fontSize: 12, color: "#9ca3af" }}>
                                                        {opt.data.fonction || "—"} · retenue {opt.data.taux ?? 5}%
                                                    </div>
                                                </div>
                                            )}
                                        />

                                        {expert && (
                                            <>
                                                <div style={{
                                                    marginTop: 14, padding: 12, borderRadius: 10, background: "#f5f5ff",
                                                    display: "flex", alignItems: "center", gap: 12,
                                                }}>
                                                    <Avatar size={42} style={{ background: PRIMARY }}>
                                                        {(expert.prenom?.[0] || "") + (expert.nom?.[0] || "")}
                                                    </Avatar>
                                                    <div>
                                                        <div style={{ fontWeight: 600, color: PRIMARY }}>{expert.prenom} {expert.nom}</div>
                                                        <div style={{ fontSize: 12, color: "#6b7280" }}>{expert.fonction || "Expert"}</div>
                                                    </div>
                                                </div>

                                                <div style={{ marginTop: 14 }}>
                                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                                        <PercentageOutlined /> Taux de retenue pour ce règlement
                                                    </Text>
                                                    <Select
                                                        style={{ width: "100%", marginTop: 6 }} value={tauxRetenue}
                                                        onChange={setTauxRetenue}
                                                        options={[
                                                            { value: 3, label: "3 %" },
                                                            { value: 5, label: "5 %" },
                                                            { value: 20, label: "20 %" },
                                                        ]}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </Card>

                                    {/* Dossiers disponibles */}
                                    <Card
                                        bordered={false}
                                        style={{ borderRadius: 14, marginTop: 18, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
                                        title={
                                            <Space>
                                                <FileTextOutlined style={{ color: PRIMARY }} />
                                                <span>2. Dossiers disponibles</span>
                                                {expert && <Tag color="default">{dossiersDisponibles.length}</Tag>}
                                            </Space>
                                        }
                                    >
                                        {!expert ? (
                                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sélectionnez d'abord un expert" />
                                        ) : (
                                            <>
                                                <Input
                                                    allowClear prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                                                    placeholder="Rechercher un dossier…" value={searchDossier}
                                                    onChange={(e) => setSearchDossier(e.target.value)} style={{ marginBottom: 12 }}
                                                />
                                                <List
                                                    loading={loadingDossiers}
                                                    dataSource={dossiersDisponibles}
                                                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={sinistres.length === 0 ? "Aucun dossier pour cet expert" : "Tous les dossiers ont été ajoutés"} /> }}
                                                    style={{ maxHeight: 360, overflowY: "auto" }}
                                                    renderItem={(d) => (
                                                        <List.Item
                                                            style={{ padding: "10px 4px" }}
                                                            actions={[
                                                                <Button key="add" type="primary" ghost size="small" icon={<PlusOutlined />} onClick={() => ajouter(d)}>
                                                                    Ajouter
                                                                </Button>,
                                                            ]}
                                                        >
                                                            <List.Item.Meta
                                                                title={
                                                                    <span style={{ fontFamily: "monospace" }}>
                                                                        {dossierNum(d)}
                                                                        {dossierCie(d) && (
                                                                            <span style={{ display: "block", fontSize: 11, color: "#9ca3af", fontWeight: 400, marginTop: 2 }}>
                                                                                N°&nbsp;cie&nbsp;: {dossierCie(d)}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                }
                                                                description={<span style={{ fontSize: 12 }}>{d.type ? `${d.type} · ` : ""}SAP&nbsp;: {fcfa(d.sap)}</span>}
                                                            />
                                                        </List.Item>
                                                    )}
                                                />
                                            </>
                                        )}
                                    </Card>
                                </Col>

                                {/* ── Colonne droite : panier + récap ── */}
                                <Col xs={24} lg={15}>
                                    <Card
                                        bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}
                                        title={
                                            <Space>
                                                <SaveOutlined style={{ color: PRIMARY }} />
                                                <span>3. Dossiers à régler</span>
                                                <Tag color={selection.length ? "processing" : "default"}>{selection.length}</Tag>
                                                {erreurs > 0 && <Tag color="error">{erreurs} en erreur</Tag>}
                                            </Space>
                                        }
                                    >
                                        <Table
                                            rowKey="codeId" size="middle" columns={columns} dataSource={selection}
                                            pagination={false} scroll={{ x: "max-content" }}
                                            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ajoutez des dossiers depuis la liste de gauche" /> }}
                                        />

                                        {selection.length > 0 && (
                                            <>
                                                <Divider style={{ margin: "18px 0" }} />
                                                <Row gutter={[16, 16]} align="middle">
                                                    <Col xs={24} md={16}>
                                                        <div style={{
                                                            background: "#f8fafc", borderRadius: 12, padding: "16px 18px",
                                                            border: "1px solid #eef2f7",
                                                        }}>
                                                            <Row gutter={[16, 12]}>
                                                                <Col span={12}><Statistic title="Total honoraires" value={totaux.totalHonoraires} suffix="FCFA" valueStyle={{ fontSize: 16 }} groupSeparator=" " /></Col>
                                                                <Col span={12}><Statistic title="Total frais" value={totaux.totalFrais} suffix="FCFA" valueStyle={{ fontSize: 16 }} groupSeparator=" " /></Col>
                                                                <Col span={12}><Statistic title="Montant brut" value={totaux.totalBrut} suffix="FCFA" valueStyle={{ fontSize: 16, color: PRIMARY }} groupSeparator=" " /></Col>
                                                                <Col span={12}><Statistic title={`Retenue honoraires (${tauxRetenue}%)`} value={totaux.montantRetenue} prefix="-" suffix="FCFA" valueStyle={{ fontSize: 16, color: DANGER }} groupSeparator=" " /></Col>
                                                            </Row>
                                                        </div>
                                                    </Col>
                                                    <Col xs={24} md={8}>
                                                        <div style={{
                                                            background: `linear-gradient(135deg, ${SUCCESS} 0%, #15803d 100%)`,
                                                            borderRadius: 12, padding: "18px 20px", color: "#fff", textAlign: "center",
                                                        }}>
                                                            <div style={{ fontSize: 13, opacity: 0.9 }}>Montant net à payer</div>
                                                            <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{fcfa(totaux.totalNet)}</div>
                                                        </div>
                                                    </Col>
                                                </Row>

                                                <div style={{ marginTop: 18, textAlign: "right" }}>
                                                    <Button
                                                        type="primary" size="large" icon={<SaveOutlined />}
                                                        loading={saving} disabled={!peutEnregistrer} onClick={enregistrer}
                                                        style={{ background: peutEnregistrer ? SUCCESS : undefined, border: "none", borderRadius: 10, fontWeight: 600 }}
                                                    >
                                                        Enregistrer le règlement
                                                    </Button>
                                                    {erreurs > 0 && (
                                                        <div style={{ marginTop: 8, color: DANGER, fontSize: 13 }}>
                                                            <WarningFilled /> {erreurs} dossier(s) dépassent le SAP autorisé.
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </Card>
                                </Col>
                            </Row>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default GestionPaiementSinistreExpertsV2;
